import { useRef, type KeyboardEvent } from 'react';
import { cx } from '@/core/utils';

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
}

interface Props<T extends string> {
  options: readonly SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Smaller buttons for inline use inside a field row. */
  compact?: boolean;
  className?: string;
}

const JIGGLE: Keyframe[] = [
  { transform: 'translateX(0)' },
  { transform: 'translateX(-3px)' },
  { transform: 'translateX(3px)' },
  { transform: 'translateX(-2px)' },
  { transform: 'translateX(0)' },
];

const NEXT_KEYS: Record<string, number> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

/**
 * A switch between mutually exclusive options: a thumb slides to the selected one. With two options
 * a tap anywhere flips it; with more, tapping the selected option only jiggles the thumb.
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  compact,
  className,
}: Props<T>) {
  const thumb = useRef<HTMLSpanElement>(null);
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const index = options.findIndex((o) => o.value === value);
  const n = options.length;

  const select = (i: number, focus = false) => {
    onChange(options[i].value);
    if (focus) buttons.current[i]?.focus();
  };

  const jiggle = () => {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    for (const el of [thumb.current, buttons.current[index]])
      el?.animate(JIGGLE, { duration: 280, easing: 'ease-out' });
  };

  const click = (i: number) => {
    if (i !== index) select(i);
    else if (n === 2) select(1 - i);
    else jiggle();
  };

  const keyDown = (e: KeyboardEvent) => {
    const step = NEXT_KEYS[e.key];
    const target = step ? (index + step + n) % n : e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : -1;
    if (target < 0) return;
    e.preventDefault();
    select(target, true);
  };

  return (
    <span
      className={cx(
        'relative grid auto-cols-fr grid-flow-col rounded-lg bg-neutral-200/80 p-0.5 shadow-inner',
        className,
      )}
      role="radiogroup"
      onKeyDown={keyDown}
    >
      {index >= 0 && (
        <span
          ref={thumb}
          aria-hidden
          className="absolute inset-y-0.5 left-0.5 rounded-md bg-accent shadow-sm motion-safe:transition-[translate] motion-safe:duration-200 motion-safe:ease-[cubic-bezier(0.3,1.3,0.5,1)]"
          style={{ width: `calc((100% - 4px) / ${n})`, translate: `${index * 100}% 0` }}
        />
      )}
      {options.map((o, i) => (
        <button
          key={o.value}
          ref={(el) => {
            buttons.current[i] = el;
          }}
          type="button"
          role="radio"
          aria-checked={i === index}
          tabIndex={i === index || (index < 0 && i === 0) ? 0 : -1}
          onClick={() => click(i)}
          className={cx(
            'relative cursor-pointer rounded-md px-2 font-medium whitespace-nowrap select-none motion-safe:transition-colors motion-safe:duration-200',
            compact ? 'h-9 md:h-7' : 'h-10 md:h-8',
            i === index ? 'text-white' : 'text-neutral-700 hover:text-neutral-950',
          )}
        >
          {o.label}
        </button>
      ))}
    </span>
  );
}
